import React, { PropsWithChildren, useReducer } from 'react';
import { ApplicationState, QuestionState, SectionState } from '@aidkitorg/types/lib/convoai';

export type ApplicationStateEvent = {
  type: 'change',
  questionId: string,
  update: Partial<QuestionState>
} | {
  type: 'update-section',
  sectionId: string,
  update: Partial<SectionState>
} | {
  type: 'update-info',
  update: ApplicationState['info']
} | {
  type: 'update-survey-details',
  update: { currentSurveyPath: string }
} | {
  type: 'transform',
  questionId: string,
  transform(q: QuestionState): QuestionState
}

export function useApplicationStateReducer(initial?: ApplicationState) {

  const [state, dispatch] = useReducer((state: ApplicationState, event: ApplicationStateEvent) => {
    switch (event.type) {
      case 'transform': {

        const question = state.questions[event.questionId];

        if (!question) {
          return state;
        }

        const updatedQuestion = event.transform(question);

        return {
          ...state,
          questions: {
            ...state.questions,
            [event.questionId]: updatedQuestion
          }
        }

      }
      case 'change':
        let currentQuestion = state.currentQuestion;
        const question = state.questions[event.questionId];
        if (question?.isNext) {
          currentQuestion = { ...question, ...event.update };
        }
        return {
          ...state,
          currentQuestion,
          questions: {
            ...state.questions,
            [event.questionId]: {
              ...(state.questions[event.questionId] ?? {}),
              ...event.update
            }
          }
        };
      case 'update-section':
        let currentSection = state.currentSection;
        const section = state.sections[event.sectionId];
        if (section?.viewing) {
          currentSection = { ...section, ...event.update };
        }
        return {
          ...state,
          currentSection,
          sections: {
            ...state.sections,
            [event.sectionId]: {
              ...(state.sections[event.sectionId] ?? {}),
              ...event.update
            }
          }
        };
      case 'update-info':
        return {
          ...state,
          questions: Object.entries(state.questions).reduce(
            (acc, [questionId, question]) => {
              if (!question) {
                return acc;
              }
              if (!question.targetField) {
                acc[questionId] = question;
                return acc;
              }
              acc[questionId] = {
                ...question,
                value: (event.update ?? {})[question.targetField] ?? question.value
              }
              return acc;
            },
            {} as Record<string, QuestionState>
          ),
          info: {
            ...(state.info ?? {}),
            ...event.update
          }
        }
      case 'update-survey-details':
        return {
          ...state,
          currentSurveyPath: event.update.currentSurveyPath
        }
      default:
        return state;
    }
  }, initial ?? { questions: {}, sections: {} });

  return {
    applicationStateChanged: dispatch,
    state
  };
}

export type ApplicationStateContextType = ReturnType<typeof useApplicationStateReducer>;

export const ApplicationStateContext = React.createContext<ApplicationStateContextType>({
  applicationStateChanged: (event) => console.warn('no QuestionStateReducer in context, ignorning:', event),
  state: {
    questions: {},
    sections: {}
  }
});

export const ApplicationStateProvider = ({ children }: PropsWithChildren) => {
  const reducer = useApplicationStateReducer();
  return <ApplicationStateContext.Provider value={reducer}>
    {children}
  </ApplicationStateContext.Provider>
}
